/**
 * Данные исполнителя в заявке на релиз: ФИО и ссылка на карточку артиста
 * (Яндекс Музыка, Spotify, VK Музыка и т.п.).
 *
 * Правила — 1-в-1 с бэком (`normalize_release_full_name` /
 * `normalize_artist_profile_url` в `release_order.py`): фронт не должен
 * пропускать то, что бэк отвергнет (422 на ровном месте), и не должен
 * отвергать то, что бэк принял бы. И отправляем ровно то, что бэк сохранит, —
 * иначе в сводке человек видел бы одно, а в заявке лежало бы другое.
 *
 * Поэтому ссылку разбираем СТРОКОЙ, повторяя `urllib.parse.urlsplit`, а не
 * через `new URL()`: WHATWG-парсер ведёт себя иначе (`\` читает как `/`,
 * `https:x.ru` принимает и т.п.), и решения фронта с бэком разъехались бы.
 *
 * Длины считаем в КОДОВЫХ ТОЧКАХ (`[...s].length`), как `len()` в Python, а не
 * в UTF-16 (`s.length`): эмодзи там — один символ, здесь — два.
 *
 * Всё здесь — чистые функции без DOM: они покрыты тестами
 * (releaseArtistProfile.test.ts), таблица кейсов сверена с бэком.
 */
/** Лимиты полей (совпадают с валидацией бэка). */
export const RELEASE_FULL_NAME_MAX = 256;
export const RELEASE_ARTIST_PROFILE_URL_MAX = 512;
/**
 * Пробел — одно определение на оба поля, как у бэка: `\t` `\n` `\r` +
 * Unicode Zs/Zl/Zp. `\v`, `\f`, `\x85` — это Cc, а не пробел.
 */
const SPACE_RE = /^[\t\n\r\p{Zs}\p{Zl}\p{Zp}]$/u;
const CONTROL_RE = /^\p{Cc}$/u;
/** Невидимые символы (U+200B, U+FEFF, bidi U+202E …) — вырезаем, а не ругаемся. */
const FORMAT_CHARS_RE = /\p{Cf}/gu;
/** «Схема уже есть» = строка начинается с `scheme://` (RFC 3986). */
const URL_SCHEME_RE = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//;
/** Метка домена: Unicode-буквы/цифры, `_`, `-` (кириллица, IPv4, punycode). */
const HOST_LABEL_RE = /^[\p{L}\p{N}_-]+$/u;
/** IPvFuture в скобках — единственное, что `urlsplit` пускает в `[…]` и что
 * потом переживает проверку меток (IPv6 всегда содержит `:`). */
const IPV_FUTURE_RE = /^v[a-fA-F0-9]+\..+$/;
const isSpace = (ch) => SPACE_RE.test(ch);
const isControl = (ch) => CONTROL_RE.test(ch);
const codePointLength = (value) => [...value].length;
function stripSpaces(value) {
    const chars = [...value];
    let start = 0;
    let end = chars.length;
    while (start < end && isSpace(chars[start]))
        start += 1;
    while (end > start && isSpace(chars[end - 1]))
        end -= 1;
    return chars.slice(start, end).join("");
}
/**
 * ФИО исполнителя: невидимые символы вырезаны, любые пробелы → один обычный,
 * по краям обрезано; пустое → `null`. Прочие управляющие символы — отказ.
 */
export function normalizeReleaseFullName(raw) {
    const chars = [];
    for (const ch of raw.replace(FORMAT_CHARS_RE, "")) {
        if (isSpace(ch))
            chars.push(" ");
        else if (isControl(ch))
            return { error: "invalid" };
        else
            chars.push(ch);
    }
    const name = chars
        .join("")
        .replace(/ {2,}/g, " ")
        .replace(/^ +| +$/g, "");
    if (!name)
        return { value: null };
    if (codePointLength(name) > RELEASE_FULL_NAME_MAX) {
        return { error: "too_long" };
    }
    return { value: name };
}
/**
 * `netloc` → хост и порт, как `_hostinfo` у `urlsplit`. `null` — netloc,
 * на котором `urlsplit` бросает (`[`/`]` не в том месте, не IPvFuture в
 * скобках, NFKC-подмена разделителей).
 */
function splitHostInfo(netloc) {
    const hostinfo = netloc.slice(netloc.lastIndexOf("@") + 1);
    const hasOpen = netloc.includes("[");
    const hasClose = netloc.includes("]");
    if (hasOpen !== hasClose)
        return null;
    let hostname;
    let port;
    const open = hostinfo.indexOf("[");
    if (open >= 0) {
        if (open > 0)
            return null;
        const bracketed = hostinfo.slice(open + 1);
        const close = bracketed.indexOf("]");
        hostname = close >= 0 ? bracketed.slice(0, close) : bracketed;
        const afterBracket = close >= 0 ? bracketed.slice(close + 1) : "";
        if (afterBracket && !afterBracket.startsWith(":"))
            return null;
        port = afterBracket.slice(1);
    }
    else {
        const colon = hostinfo.indexOf(":");
        hostname = colon >= 0 ? hostinfo.slice(0, colon) : hostinfo;
        port = colon >= 0 ? hostinfo.slice(colon + 1) : "";
        // Скобки есть, но не в хосте (например, в userinfo) — `urlsplit` бросает.
        if (hasOpen)
            return null;
    }
    if (hasOpen && !IPV_FUTURE_RE.test(hostname))
        return null;
    // `_checknetloc`: символ вроде U+2100 после NFKC превращается в `a/c` —
    // `urlsplit` на таком бросает.
    if (!/^[\x00-\x7f]*$/.test(netloc)) {
        const cleaned = netloc.replace(/[@:#?]/g, "");
        const normalized = cleaned.normalize("NFKC");
        if (normalized !== cleaned && /[/?#@:]/.test(normalized))
            return null;
    }
    return { hostname, port };
}
const isValidHostLabel = (label) => HOST_LABEL_RE.test(label) && !label.startsWith("-") && !label.endsWith("-");
/**
 * Ссылка на карточку артиста: `music.yandex.ru/artist/123` →
 * `https://music.yandex.ru/artist/123`. Пустое → `null`.
 *
 * Регистр не меняем и `href`-нормализацию не делаем: возвращается ввод без
 * невидимых символов и пробелов по краям, со схемой при необходимости.
 */
export function normalizeReleaseArtistProfile(raw) {
    const url = stripSpaces(raw.replace(FORMAT_CHARS_RE, ""));
    if (!url)
        return { value: null };
    // `\` браузеры читают как `/` (`https://evil.com\.a.ru` уводит на evil.com),
    // пробел/управляющий внутри — это уже не ссылка.
    if (url.includes("\\") || [...url].some((ch) => isSpace(ch) || isControl(ch))) {
        return { error: "invalid" };
    }
    const withScheme = URL_SCHEME_RE.test(url) ? url : `https://${url}`;
    if (codePointLength(withScheme) > RELEASE_ARTIST_PROFILE_URL_MAX) {
        return { error: "too_long" };
    }
    // Схема есть по построению (`scheme://`): до первого `:` — схема, netloc —
    // от `//` до первого `/`, `?` или `#`.
    const colon = withScheme.indexOf(":");
    const scheme = withScheme.slice(0, colon).toLowerCase();
    const netloc = withScheme.slice(colon + 3).split(/[/?#]/)[0];
    const hostInfo = splitHostInfo(netloc);
    if (!hostInfo)
        return { error: "invalid" };
    if (scheme !== "http" && scheme !== "https")
        return { error: "invalid" };
    // Пустой порт (`a.ru:`), не число, вне 1..65535.
    const { hostname, port } = hostInfo;
    if (netloc.endsWith(":"))
        return { error: "invalid" };
    if (port) {
        if (!/^[0-9]+$/.test(port))
            return { error: "invalid" };
        const portNumber = Number(port);
        if (portNumber < 1 || portNumber > 65535)
            return { error: "invalid" };
    }
    // `user:pass@` в ссылке на профиль не нужен, а подменить хост им легко.
    if (netloc.includes("@"))
        return { error: "invalid" };
    const labels = hostname.toLowerCase().split(".");
    if (labels.length < 2 || !labels.every(isValidHostLabel)) {
        return { error: "invalid" };
    }
    return { value: withScheme };
}
/**
 * Можно ли подставить сохранённое значение в `href` сводки. Значение ввёл
 * пользователь, поэтому ссылкой делаем только то, что проходит правило бэка
 * И уже начинается с http(s) — иначе `javascript:` стал бы исполняемой
 * ссылкой. Остальное показываем текстом.
 */
export function releaseArtistProfileHref(stored) {
    const trimmed = (stored ?? "").trim();
    if (!/^https?:\/\//i.test(trimmed))
        return null;
    const result = normalizeReleaseArtistProfile(trimmed);
    return "value" in result && result.value === trimmed ? trimmed : null;
}
